export type Area = 'Central' | 'Eastern' | 'Southern' | 'Western' | 'Lexus HQ';

export type UserGroup = 'Area PC' | 'Leaders';

export interface TrainingData {
  courseName: string;
  courseCode: string;
  dueDate: string;
  certLevel: string;
  percentNotComplete: number;
  participantsRemaining: number;
  area: Area;
}

export interface RiskSummary {
  losingLML: number;
  high: number;
  medium: number;
  low: number;
}

export interface ImpactStats {
  totalRemaining: number;
  totalCompliant: number;
  avgCompletion: number;
  urgentCourses: number;
  highRiskCourses: number;
  losingMasterThisMonth: number;
  losingLMLNextMonth: number;
  losingSeniorThisMonth?: number;
  certificationRequiredByDate?: string;
  paymentRequiredByDate?: string;
  riskSummary: RiskSummary
}

export interface LeadersSummaryRow {
  course: string;
  notCompleted: number;
  completion: number;
  dueDate: string;
}

export interface PredictionRow {
  course: string;
  seatsAvailable: number;
  needsToComplete: number;
  plannedAhead: number;
  extraSessions: number;
  cost: number;
}

export interface ChartRow {
  name: string;
  notComplete: number;
}

import { DataTableSchema, DefaultTableSchema, TITLE_TYPE } from './dynamic-data-table.model';
import { CommonChartModel, PieChartModel } from './chart.model';

export const ImpactAnalysisCourseTitle = 'Course Details';
export const ImpactAnalysisRiskTitle = 'Risk Summary';
export const ImpactAnalysisLeadersTitle = 'Leaders Summary';
export const ImpactAnalysisPredictionTitle = 'Training Prediction (Next Quarter)';
export const courseCode = "Course Code";
export const ASSOCIATES_PENDING_COMPLETION = "Associates Pending Completion";

export const ImpactAnalysisCourseTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  isPaginationRequired: true,
  isSearchRequired: true,
  isFooterRequired: false,
  columnSigns: {
    labelColumnText: 'courseName'
  },
  tableTitle: ImpactAnalysisCourseTitle,
  columnSchema: [
    { label: 'COURSE', fieldKey: 'course', isNotCamelCase: true },
    { label: courseCode, fieldKey: 'course_code', isNotCamelCase: true },
    { label: 'DUE DATE', fieldKey: 'due_date' },
    { label: 'LEVEL', fieldKey: 'level' },
    {
      label: ASSOCIATES_PENDING_COMPLETION,
      fieldKey: 'remaining'
    }
  ],
  dataSource: [],
  isExportRequired: true,
  exportConfig: {
    fileName: 'Impact_Analysis_Course_Details',
    exportMessage: 'Impact Analysis Course Details Data',
    schema: {
      spinId: 'SPIN ID',
      firstName: 'FIRST NAME',
      lastName: 'LAST NAME',
      areaRegion: 'AREA',
      dueDate: 'DUE DATE',
      certificationLevel: 'LEVEL',
    },
    groupedSheets: {
      useResponseKeys: true,
      schemaMap: {
        summary: {
          course: 'COURSE',
          courseCode: courseCode,
          dueDate: 'DUE DATE',
          level: 'LEVEL',
          associatesPending: ASSOCIATES_PENDING_COMPLETION,
        }
      }
    }
  }
};

export const ImpactAnalysisRiskTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  dataSource: [],
  isPaginationRequired: true,
  isSearchRequired: true,
  isExportRequired: true,
  tableTitle: 'Risk Summary', 
  columnSchema: [
    { label: 'Risk Level', fieldKey: 'risk_level' },
    { label: 'Total Associate Count', fieldKey: 'associate_count' },
  ],
  exportConfig: {
    fileName: 'Impact_Analysis_Risk_Summary',
    exportMessage: 'Impact Analysis Risk Summary Data',
    schema: {
      risk_level: 'RISK LEVEL',
      associate_count: 'TOTAL ASSOCIATE COUNT'
    }
  }
};


export const ImpactAnalysisLeadersCourseTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  isPaginationRequired: true,
  isSearchRequired: false,
  isFooterRequired: false,
  columnSigns: {
    labelColumnText: 'courseName'
  },
  tableTitle: ImpactAnalysisCourseTitle,
  columnSchema: [
    { label: 'COURSE', fieldKey: 'course', isNotCamelCase: true },
    { label:  courseCode, fieldKey: 'course_code', isNotCamelCase: true },
    { label: 'LEVEL', fieldKey: 'level' },
    { label: 'COMPLETION %', fieldKey: 'complete_percentage' },
    { label: 'ASSOCIATE INCOMPLETE COURSE %', fieldKey: 'incomplete_percentage' },
    { label:  ASSOCIATES_PENDING_COMPLETION, fieldKey: 'remaining'},
    { label: 'DUE DATE', fieldKey: 'due_date' },
  ],
  dataSource: [],
  isExportRequired: true,
  exportConfig: {
    fileName: 'Impact_Analysis_Course_Details',
    exportMessage: 'Impact Analysis Course Details Data',
    schema: {
      spinId: 'SPIN ID',
      firstName: 'FIRST NAME',
      lastName: 'LAST NAME',
      areaRegion: 'AREA',
      dueDate: 'DUE DATE',
      certificationLevel: 'LEVEL',
    },
    groupedSheets: {
      useResponseKeys: true,
      schemaMap: {
        summary: {
          course: 'COURSE',
          courseCode: courseCode,
          level: 'LEVEL',
          complete_percentage: 'COMPLETION %',
          incomplete_percentage:'ASSOCIATE INCOMPLETE COURSE %',
          associatesPending: 'ASSOCIATES_PENDING_COMPLETION',
          dueDate: 'DUE DATE',
        }
      }
    }
  }
};


export const ImpactTrainingPredictionTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),

  tableTitle: 'Training Prediction (Next Quarter)',
  isTitleCamelCase: false,
  isPaginationRequired: true,
  isSearchRequired: true,
  isExportRequired: true,

  columnSchema: [
    { label: 'COURSE NAME', fieldKey: 'course',isNotCamelCase: true },
    { label: 'SEATS AVAILABLE', fieldKey: 'available_seat' },
    { label: 'NEED TO COMPLETE', fieldKey: 'pending_associates' },
    {
      label: 'PLAN AHEAD',
      fieldKey: 'planned_ahead'
    },
    {
      label: 'UNIT COST',
      fieldKey: 'training_cost_per_class',
      isCurrency: true
    },
    {
      label: 'ESTIMATED COST',
      fieldKey: 'estimated_cost',
      isCurrency: true
    },
    { label: 'EXTRA SESSIONS', fieldKey: 'extra_sessions' }
  ],

  exportConfig: {
    fileName: 'Impact_Training_Prediction',
    exportMessage: 'Impact Training Prediction Data',
    schema: {
      course: 'COURSE NAME',
      available_seat: 'SEATS AVAILABLE',
      pending_associates: 'NEED TO COMPLETE',
      planned_ahead: 'PLAN AHEAD',
      training_cost_per_class: 'UNIT COST',
      estimated_cost: 'ESTIMATED COST',
      extra_sessions: 'EXTRA SESSIONS'
    }
  },

  dataSource: []
};


export const EmptyFilterSchema = {
  FieldsArray: structuredClone([]),
  ButtonsArray: []
};

export const AssociateCertSummaryChart: CommonChartModel = {
  ...structuredClone(PieChartModel),
  titleSchema: {
    label: '',
    title: 'Certification Overview ',
    type: TITLE_TYPE['Static']
  },
  tooltip: {
    headerFormat: '<span style="font-size:11px"><b>{point.name} </b></span><br>',
    pointFormat: `<b><span>Count -  {point.y}</span></b><br/>`
 },
  dataSchema: {
    label: 'certification',
    value: 'count'
  },
  drilldown: {
    breadcrumbs: {
      position: { align: 'right' }
    },
    series: []
  },
  exportConfig: {
    fileName: 'Impact_Analysis_Associate_Cert_Summary',
    groupedSheets: { groupByField: 'certification' }
  }
};